// VIIRS heat detection layer family.
//
// NASA VIIRS satellite-detected hot spots — one GeoJSON source drives a
// single circle layer whose color scales with the Confidence property
// (pale yellow at low confidence → dark red at highest). Mounted with
// visibility: 'none' (lazy toggle).
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 1 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer id, and
// layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addViirsLayers(map: MlMap): void {
  if (!map.getSource('viirs')) {
    map.addSource('viirs', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'viirs-circle',
      type: 'circle',
      source: 'viirs',
      layout: { visibility: 'none' },
      paint: {
        'circle-color': [
          'interpolate', ['linear'], ['to-number', ['get', 'Confidence'], 50],
          0,   '#fde68a',  // low confidence: pale yellow
          50,  '#fb923c',  // medium: orange
          80,  '#dc2626',  // high: red
          100, '#7f1d1d',  // highest: dark red
        ],
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          5, 2,
          10, 5,
          14, 8,
        ],
        'circle-stroke-color': '#0b0d0f',
        'circle-stroke-width': 1,
        'circle-opacity': 0.85,
      },
    });
  }
}

registerLayerFamily({
  id: 'viirs',
  label: 'VIIRS Heat Detections',
  sourceId: 'viirs',
  layerIds: ['viirs-circle'],
  mount: (map) => addViirsLayers(map),
  visibilitySelector: (l) => l.viirs,
  applyState: (map) => {
    if (!map.getLayer('viirs-circle')) return;
    const show = useLayerVisibilityStore.getState().layers.viirs;
    map.setLayoutProperty('viirs-circle', 'visibility', show ? 'visible' : 'none');
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('viirs');
    if (!spec || isOverlayLoaded('viirs')) return;
    markOverlayLoaded('viirs');
    return refreshOverlay(map, spec);
  },
  bboxRefresh: (map) => {
    if (!isOverlayLoaded('viirs')) return;
    const spec = OVERLAY_BY_ID.get('viirs');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
